import {css, html, MutableElement, nothing, ifDefined, ifDefinedThen} from "Application.js";
import {Directive, directive} from "lit/directive.js";
import {repeat} from "lit/directives/repeat.js";
import {getMoneyDecimal, formatMoney} from "./profile-finance-grid.js";

export function fixFloating(number) {
	return Math.round((number + Number.EPSILON) * 100) / 100;
}

class FormatFactor extends Directive {

	render(factor) {
		if(!Utils.isSet(factor))
			return html`-`;
		return html`${fixFloating(factor * 100) + "%"}`;
	}

}

export const formatFactor = directive(FormatFactor);

class FormatShare extends Directive {

	render(value) {
		if(!Utils.isSet(value))
			return html`-`;
		return html`${(value * 100).toFixed(2) + "%"}`;
	}

}

export const formatShare = directive(FormatShare);

export function calcCashFlow(waac, pow, freeCashFlow) {
	const discounting = 1 / (1 + waac) ** pow;
	return {
		discounting: discounting,
		cashFlow: freeCashFlow * discounting,
	};
}

export function totalDCF(year, valuation, freeCashFlow) {
	if(valuation && valuation.WAAC && valuation.LongTermGrowth && valuation.LongTermGrowth < valuation.WAAC) {
		let y1 = year;
		let y2 = year + 1;
		let y3 = year + 2;
		let y4 = year + 3;
		let y5 = year + 4;

		if(freeCashFlow[y1] && freeCashFlow[y2] && freeCashFlow[y3] && freeCashFlow[y4] && freeCashFlow[y5]) {
			const t1 = calcCashFlow(valuation.WAAC, 1, freeCashFlow[y1]);
			const t2 = calcCashFlow(valuation.WAAC, 2, freeCashFlow[y2]);
			const t3 = calcCashFlow(valuation.WAAC, 3, freeCashFlow[y3]);
			const t4 = calcCashFlow(valuation.WAAC, 4, freeCashFlow[y4]);
			const t5 = calcCashFlow(valuation.WAAC, 5, freeCashFlow[y5]);

			const finalFreeCashFlow = freeCashFlow[y5] * (1 + valuation.LongTermGrowth);
			const finalDiscounting = 1 / (valuation.WAAC - valuation.LongTermGrowth) * t5.discounting;
			const finalCashFlow = finalFreeCashFlow * finalDiscounting;

			const final = (t1.cashFlow + t2.cashFlow + t3.cashFlow + t4.cashFlow + t5.cashFlow) + finalCashFlow;

			return parseFloat(final.toFixed(2));
		}
	}

	return null;
}

export function totalMultiples(valuation) {
	let sum = 0;
	let total = 0;

	if(valuation) {
		if(valuation.RevenueMultiple && valuation.RevenueValue) {
			sum += valuation.RevenueValue * valuation.RevenueMultiple;
			total++;
		}

		if(valuation.EBITMultiple && valuation.EBITValue) {
			sum += valuation.EBITValue * valuation.EBITMultiple;
			total++;
		}
	}

	return total > 0 ? parseFloat((sum / total).toFixed(2)) : null;
}

export function calcShare(valuation) {
	if(valuation && valuation.Investment && valuation.Investment.Amount && valuation.ReturnMultiple && valuation.HorizonRevenueValue && valuation.HorizonEBITValue) {
		const exitValue = ((valuation.RevenueMultiple * valuation.HorizonRevenueValue) + (valuation.EBITMultiple * valuation.HorizonEBITValue)) / 2;
		const investment = getMoneyDecimal(valuation.Investment.Amount);
		if(investment > 0) {
			if(exitValue > 0) {
				return investment * valuation.ReturnMultiple / exitValue;
			}
		}
	}

	return null;
}

export function totalVC(valuation) {
	const share = calcShare(valuation);
	if(share) {
		const investment = getMoneyDecimal(valuation.Investment.Amount);
		return (investment / share) - investment;
	}

	return null;
}

export function totalMarket(markets) {
	let sum = 0;
	let totalCompanies = 0;

	if(markets && markets.length) {
		markets.forEach(market => {
			if(market.Value && market.Factor) {
				sum += market.Value * market.Factor;
				totalCompanies++;
			}
		});
	}

	return totalCompanies > 0 ? parseFloat((sum / totalCompanies).toFixed(2)) : null;
}

let AUTO_ID = 0;

const QUERY = `
	fragment FinanceValuation on Finance {
		# DCF Data
		PNLValues(fromYear: $fromYear, toYear: $toYear, filter: { Type: FREE_CASH_FLOW }) @include(if: $withDCFData) {
			Year
			Value {Amount}
		}
		
		Valuation(fromYear: $fromYear) {
			# DCF
			WAAC, WAACComment
			LongTermGrowth, LongTermGrowthComment
			
			# Multiples
			RevenueMultiple, RevenueMultipleComment
			EBITMultiple, EBITMultipleComment
			RevenueValue
			EBITValue
			
			# VC
			Investment {Amount}, InvestmentComment
			Horizon, HorizonComment
			HorizonRevenueValue(fromYear: $horizonFromYear)
		    HorizonEBITValue(fromYear: $horizonFromYear)
			ReturnMultiple, ReturnMultipleComment
		}
	}

	fragment FinanceValuationMarkets on Finance {
		ValuationMarkets {
			ID
			CompanyName
			Value
			Factor
			Comment
		}
	}
	
	query ProfileFinanceValuation($profileID: ID!, $fromYear: Int!, $toYear: Int = null, $horizonFromYear: Int = null, $withDCFData: Boolean!, $withMarkets: Boolean!) {
		getProfile(id: $profileID) {
			... on CompanyProfile {
				Finance {
					...FinanceValuation
					...FinanceValuationMarkets @include(if: $withMarkets)
				}
			}
			... on ShareholderProfile {
				Finance {
					...FinanceValuation
					...FinanceValuationMarkets @include(if: $withMarkets)
				}
			}
		}
	}
`;

const MAX_COMMENT_LENGTH = 255;

export class ProfileFinanceValuation extends MutableElement {

	static get properties() {
		return {
			mode: { type: String, reflect: true },
			profileID: { type: String, attribute: "profile-id", reflect: true },
			year: { type: Number, reflect: true },
			loading: { type: Boolean, attribute: false },
			isHorizonLoading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			data: { type: Array, attribute: false }
		};
	}

	constructor() {
		super();

		this.mode = "read";

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;
		this.isHorizonLoading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		// Current year -/+ 5
		this.years = [];
		this.year = new Date().getFullYear();

		let year = this.year-5;
		while(year <= this.year+5) {
			this.years.push(year);
			year++;
		}

		/**
		 * The rows data.
		 * [{ID: string, CompanyName: string, Value: int, Factor: float}, ...]
		 */
		this.data = { Markets: [] };

		/**
		 * Map list of already loaded years.
		 *
		 * {Year: true}
		 */
		this._loadedYears = {};
		this._loadedHorizons = {};
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.load(this.year, true));
				break;

			case "year":
				if(value)
					requestAnimationFrame(() => this.load(value, false));
				break;
		}
	}

	reset() {
		this.mode = "read";
		this.error = null;
		this.loading = false;
		this.data = { Markets: [] };
		this._loadedYears = {};
		this._initialData = {};
		this._changes = {};
	}

	refresh() {
		this._loadedYears = {};
		this._loadedHorizons = {};
		this.error = null;
		this.data = { Markets: [] };
		this._initialData = {};
		this.load(this.year, true);
	}

	add() {
		this.data.Markets.push({ __ID__: AUTO_ID++ });

		// Update DOM
		this.requestUpdate();

		// Focus
		requestAnimationFrame(() => {
			const lastEditInput = this.shadowRoot.querySelector(".edit:nth-last-of-type(6) > paper-input");
			if(lastEditInput !== null)
				lastEditInput.focus();
		});
	}

	removeByIndex(index) {
		if(!this.data.Markets[index])
			return;

		const market = this.data.Markets[index];

		if(market.__ID__ !== undefined)
			this.removeValueByPath("Markets.Added." + market.__ID__);
		else
			this.setValueByPath("Markets.Removed." + market.ID + ".ID", market.ID);

		this.data.Markets.splice(index, 1);

		// Update DOM
		this.requestUpdate();

		this._dispatchEvent();
	}

	getChanges() {
		const changes = super.getChanges();

		if(Object.keys(changes).length > 0) {
			// Convert Map {Year: Data} to array [{Year: Year, ...Data}]
			const years = [];

			for(let year in changes) {
				if(year === "Markets")
					continue;

				years.push({ Year: parseInt(year), ...changes[year] });
				delete changes[year];
			}

			if(years.length > 0)
				changes.Years = years;
		}

		return {
			Finance: {
				Valuation: changes
			}
		}
	}

	resetChanges() {
		if(!this.hasChanges())
			return;

		this.data = this.cloneSimpleValue(this._initialData);

		this._changes = {};

		// Update DOM
		this.requestUpdate();

		this._dispatchEvent();
	}

	saveChanges() {
		if(!this.hasChanges())
			return;

		this.data = {};
		this._loadedYears = {};
		this._initialData = {};
		this._changes = {};

		this.load(this.year, true);

		this._dispatchEvent();
	}

	_onValueChange(value, year, property) {
		if(value === "")
			value = null;

		this.setValueByPath(year + "." + property, value, this.data);

		// Change detection
		const initValue = this.getValueByPath(year + "." + property, this._initialData);
		if(value !== initValue && !(value === null && initValue === undefined)) {
			this.setValueByPath(year + "." + property, value);
		} else {
			this.removeValueByPath(year + "." + property);
		}

		// Update DOM
		this.requestUpdate();

		if(property === "Horizon")
			this.loadHorizon(year, value);

		this._dispatchEvent();
	}

	_onMarketValueChange(value, market, property) {
		if(value === "")
			value = null;

		market[property] = value;

		// Change detection
		if(market.__ID__ !== undefined) {
			this.setValueByPath("Markets.Added." + market.__ID__ + "." + property, value);
		} else {
			const initValue = this._initialData.Markets.find(m => m.ID === market.ID)[property];
			if(value !== initValue) {
				this.setValueByPath("Markets.Changed." + market.ID + ".ID", market.ID);
				this.setValueByPath("Markets.Changed." + market.ID + "." + property, value);
			} else {
				this.removeValueByPath("Markets.Changed." + market.ID + ".ID");
				this.removeValueByPath("Markets.Changed." + market.ID + "." + property);
			}
		}

		// Update DOM
		this.requestUpdate();

		this._dispatchEvent();
	}

	_dispatchEvent() {
		this.dispatchEvent(new CustomEvent("profile-finance-valuation-changed", {
			composed: true,
			detail: {
				value: this.hasChanges()
			}
		}));
	}

	_onYearChanged(year) {
		this.year = year;

		this.dispatchEvent(new CustomEvent("profile-finance-valuation-year-change", {
			composed: true,
			detail: {
				target: this,
				year: year
			}
		}));
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(year, withMarkets) {
		// Skip, because year is already loaded.
		if(this._loadedYears.hasOwnProperty(year))
			return;

		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const variables = {
			profileID: this.profileID,
			fromYear: Number(year),
			toYear: Number(year + 4),
			withDCFData: true,
			withMarkets: withMarkets
		};

		//this.loading = true;

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: QUERY,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			this.data.FreeCashFlow = {};
			this.data[year] = {};
			this._initialData[year] = {};

			if(response.data.getProfile && response.data.getProfile.Finance) {
				if(response.data.getProfile.Finance.PNLValues) {
					// Holds all merged Free Cash Flow values
					const freeCashFlow = {};

					response.data.getProfile.Finance.PNLValues.forEach(pnlValue => {
						if(pnlValue.Value) {
							if(!freeCashFlow.hasOwnProperty(pnlValue.Year))
								freeCashFlow[pnlValue.Year] = 0;
							freeCashFlow[pnlValue.Year] += parseFloat(pnlValue.Value.Amount);
						}
					});

					// Adds the values if not 0
					for(let year in freeCashFlow) {
						if(freeCashFlow[year])
							this.data.FreeCashFlow[year] = freeCashFlow[year];
					}
				}

				if(response.data.getProfile.Finance.Valuation) {
					this.data[year] = response.data.getProfile.Finance.Valuation;
					this._initialData[year] = this.cloneSimpleValue(this.data[year]);
				}

				if(withMarkets && response.data.getProfile.Finance.ValuationMarkets) {
					this.data.Markets = response.data.getProfile.Finance.ValuationMarkets;
					this._initialData.Markets = this.cloneSimpleValue(this.data.Markets);
				}
			}

			// Cache year as loaded
			this._loadedYears[year] = true;

			// Update DOM
			this.requestUpdate();
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	loadHorizon(year, horizon) {
		const horizonFromYear = year + (horizon === null || horizon === undefined ? 0 : horizon);

		// Skip, because year is already loaded.
		if(this._loadedHorizons.hasOwnProperty(horizonFromYear)) {
			this.data[year].HorizonRevenueValue = this._loadedHorizons[horizonFromYear].HorizonRevenueValue;
			this.data[year].HorizonEBITValue = this._loadedHorizons[horizonFromYear].HorizonEBITValue;
			return;
		}

		this.isHorizonLoading = true;

		const variables = {
			profileID: this.profileID,
			fromYear: year,
			horizonFromYear: horizonFromYear,
			withDCFData: false,
			withMarkets: false
		};

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: QUERY,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			if(response.data.getProfile && response.data.getProfile.Finance) {
				if(response.data.getProfile.Finance.Valuation) {
					this.data[year].HorizonRevenueValue = response.data.getProfile.Finance.Valuation.HorizonRevenueValue;
					this.data[year].HorizonEBITValue = response.data.getProfile.Finance.Valuation.HorizonEBITValue;

					// Cache year as loaded
					this._loadedHorizons[horizonFromYear] = {
						HorizonRevenueValue: response.data.getProfile.Finance.Valuation.HorizonRevenueValue,
						HorizonEBITValue: response.data.getProfile.Finance.Valuation.HorizonEBITValue,
					};

					// Update DOM
					this.requestUpdate();
				}
			}
		})
		.catch(reason => this.error = reason)
		.finally(() => this.isHorizonLoading = false);
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				padding-bottom: 16px;
			}
			
			.grid-table {
				display: grid;
				grid-template-columns: repeat(2, 2fr) 3fr;
			}
			
			/* Affects all first column cells (the first and every 3th item) */
			.grid-table > :nth-child(3n+1) {
				text-align: left;
				border-left: none;
			}
			
			.grid-table > div {
				display: flex;
				flex-direction: column;
			    justify-content: center;
			    padding: 6px 24px;
			    border-bottom: 1px solid var(--divider-color);
			    border-left: 1px solid var(--divider-color);
			    text-align: center;
			}
			
			.column {
				position: relative;
				padding: 16px 24px;
				
				/* --paper-font-body2 */
				font-size: 14px;
		        font-weight: 500;
		        line-height: 24px;
			}
			
			.column.dropdown {
				padding: 0 6px;
			}
			
			.column.dropdown > x-dropdown {
				/* Hack */
				--paper-input-container_-_padding: 0px;
			}
			
			.cell {			
				/* Hack */
				--paper-input-container_-_padding: 0px;
				
				/* --paper-font-body1 */
				font-size: 14px;
		        font-weight: 400;
		        line-height: 20px;
			}
			
			.cell.head {	
				/* --paper-font-body2 */
				font-size: 14px;
		        font-weight: 500;
		        line-height: 24px;
			}
			
			.cell.head.border {
				border-top: 1px solid var(--divider-color);
			}
			
			.cell.edit {
				padding-right: 6px;
			}
			
			.cell.edit.value {
				flex-direction: row;
				padding: 6px;
			}
			
			.cell.edit > span {
				margin: 0 8px;
			}
			
			.cell.edit > number-input,
			.cell.edit > paper-input {
				width: 100%;
			}
			
			.cell > .button {
				color: var(--primary-color);
				cursor: pointer;
			}
			
			.cell.comment {
				flex-direction: row;
				align-items: center;
				justify-content: flex-start;
				text-align: left;
				padding: 0 6px;
			}
			
			.cell.comment > paper-input {
				flex: 1;
			}
			
			.cell.comment > .buttons {
				background: var(--divider-color);
				width: 1px;
				height: 100%;
				margin: 0 6px;
			}
			
			.cell.comment > paper-icon-button {
				color: var(--error-color);
			}
			
			.cell paper-icon-button {
				padding: 4px;
				width: 32px;
				height: 32px;
			}
			
			.cell paper-spinner-lite {
				align-self: center;
				width: 16px;
				height: 16px;
				--paper-spinner-stroke-width: 2px;
			}
			
			.loading {
				display: flex;
				flex-direction: column;
				padding: 32px 16px 16px;
			}
			
			.loading > paper-spinner-lite {
				align-self: center;
			}
			
			.loading > .error {
				color: var(--error-color);
			}
		`;
	}

	render() {
		return html`
            <div class="container">                
                ${(this.loading || this.error !== null) ? html`
                    <div class="loading">
						${this.loading ? html`<paper-spinner-lite active></paper-spinner-lite>` : nothing}
						${this.error !== null ? html`<div class="error">Error: ${this.error}</div>` : nothing}
					</div>
                ` : html`
	                <section class="grid-table">
	                    <!-- Columns -->
	                    <div class="column">Method</div>
	                    <div class="column dropdown">
	                        <x-dropdown id="YearDropdown" no-label-float .value=${this.year} @value-selected=${e => this._onYearChanged(e.target.value)}>
	                            ${this.years.map((year) => html`<x-option .value=${year}>${year}</x-option>`)}
	                        </x-dropdown>
	                    </div>
	                    <div class="column">Pre Money Valuation</div>
	
	                    <!-- Rows -->
	                    
	                    <!-- DCF -->
	                    ${this._htmlDCF()}
	                    
	                    <!-- Multiples -->
	                    ${this._htmlMultiples()}
	                    
	                    <!-- VC -->
	                    ${this._htmlVC()}
		                
	                    <!-- Market -->
		                ${this._htmlMarket()}
	                </section>
                `}
            </div>
		`;
	}

	updated(_changedProperties) {
		// Custom style for shadow elements which cannot be styled by css
		const dropdown = this.shadowRoot.querySelector("#YearDropdown");
		if(dropdown !== null) {
			const paperRipple = dropdown.shadowRoot.querySelector("paper-ripple");
			paperRipple.style.bottom = "0";

			const container = dropdown.shadowRoot.querySelector(".container");
			container.style.marginTop = "0";
		}
	}

	_htmlDCF() {
		return html`
			<div class="cell head">DCF</div>
            <div class="cell head">Value</div>
            <div class="cell head">${formatMoney(totalDCF(this.year, this.data[this.year], this.data.FreeCashFlow))}</div>
            
            <!-- WAAC -->
            <div class="cell">WAAC (%)</div>
			${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="%" pattern="^[0-9]+((\.|,)[0-9]{1,2})?$" allowed-pattern="[0-9.,]" required .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].WAAC, fixFloating(this.data[this.year].WAAC * 100)) : null} @change=${e => this._onValueChange(e.target.value / 100, this.year, "WAAC")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].WAACComment, this.data[this.year].WAACComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "WAACComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? formatFactor(this.data[this.year].WAAC) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].WAACComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
            
            <!-- Long-term growth -->
            <div class="cell">Long-term growth (%)</div>
            ${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="%" pattern="^[0-9]+((\\.|,)[0-9]{1,2})?$" allowed-pattern="[0-9.,]" required .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].LongTermGrowth, fixFloating(this.data[this.year].LongTermGrowth * 100)) : null} @change=${e => this._onValueChange(e.target.value / 100, this.year, "LongTermGrowth")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].LongTermGrowthComment, this.data[this.year].LongTermGrowthComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "LongTermGrowthComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? formatFactor(this.data[this.year].LongTermGrowth) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].LongTermGrowthComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
		`;
	}

	_htmlMultiples() {
		return html`
			<div class="cell head border">Multiples</div>
            <div class="cell head border">Value</div>
            <div class="cell head border">${formatMoney(totalMultiples(this.data[this.year]))}</div>
            
            <!-- Revenue Multiples -->
            <div class="cell">Revenue Multiple</div>
			${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="Multiple" pattern="^[0-9]+((\.|,)[0-9]{1})?$" allowed-pattern="[0-9.,]" .value=${this._loadedYears[this.year] ? this.data[this.year].RevenueMultiple : null} @change=${e => this._onValueChange(e.target.value, this.year, "RevenueMultiple")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].RevenueMultipleComment, this.data[this.year].RevenueMultipleComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "RevenueMultipleComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? (this.data[this.year].RevenueMultiple ? this.data[this.year].RevenueMultiple : "-") : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].RevenueMultipleComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
            
            <!-- EBIT Multiples -->
            <div class="cell">EBIT Multiple</div>
            ${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="Multiple" pattern="^[0-9]+((\.|,)[0-9]{1})?$" allowed-pattern="[0-9.,]" .value=${this._loadedYears[this.year] ? this.data[this.year].EBITMultiple : null} @change=${e => this._onValueChange(e.target.value, this.year, "EBITMultiple")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].EBITMultipleComment, this.data[this.year].EBITMultipleComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "EBITMultipleComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? (this.data[this.year].EBITMultiple ? this.data[this.year].EBITMultiple : "-") : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].EBITMultipleComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
		`;
	}

	_htmlVC() {
		return html`
			<div class="cell head border">VC</div>
	        <div class="cell head border">Value</div>
	        <div class="cell head border">${!this.isHorizonLoading ? formatMoney(totalVC(this.data[this.year])) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
	        
	        <!-- Investment -->
	        <div class="cell">Investment</div>
			${this.mode === "write" ? html`
				<div class="cell edit value">
					<paper-input type="number" no-label-float placeholder="-" allowed-pattern="[0-9.]" auto-validate .value=${this._loadedYears[this.year] && this.data[this.year].Investment ? this.data[this.year].Investment.Amount : null} @change=${e => this._onValueChange(e.target.value, this.year, "Investment.Amount")}></paper-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].InvestmentComment, this.data[this.year].InvestmentComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "InvestmentComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? formatMoney(this.data[this.year].Investment ? this.data[this.year].Investment.Amount : null) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].InvestmentComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
	        
	        <!-- Horizon -->
	        <div class="cell">Horizon</div>
			${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="-" allowed-pattern="[0-9]" .value=${this._loadedYears[this.year] ? this.data[this.year].Horizon : null} @change=${e => this._onValueChange(e.target.value, this.year, "Horizon")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].HorizonComment, this.data[this.year].HorizonComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "HorizonComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? (Utils.isSet(this.data[this.year].Horizon) ? this.data[this.year].Horizon : "-") : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].HorizonComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
	        
	        <!-- Return Multiple -->
	        <div class="cell">Return Multiple</div>
			${this.mode === "write" ? html`
				<div class="cell edit value">
					<number-input no-label-float placeholder="-" pattern="^[0-9]+((\.|,)[0-9]{1})?$" allowed-pattern="[0-9.,]" .value=${this._loadedYears[this.year] ? this.data[this.year].ReturnMultiple : null} @change=${e => this._onValueChange(e.target.value, this.year, "ReturnMultiple")}></number-input>
				</div>
				<div class="cell edit value comment">
					<paper-input maxlength=${MAX_COMMENT_LENGTH} no-label-float placeholder="Comment" .value=${this._loadedYears[this.year] ? ifDefinedThen(this.data[this.year].ReturnMultipleComment, this.data[this.year].ReturnMultipleComment) : null} @change=${e => this._onValueChange(e.target.value, this.year, "ReturnMultipleComment")}></paper-input>
				</div>
			` : html`
				<div class="cell">${this._loadedYears[this.year] ? (Utils.isSet(this.data[this.year].ReturnMultiple) ? this.data[this.year].ReturnMultiple : "-") : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
				<div class="cell comment">${this._loadedYears[this.year] ? this.data[this.year].ReturnMultipleComment : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
			`}
	        
	        <!-- Calculated Share -->
	        <div class="cell">Calculated Share (%)</div>
			<div class="cell">${this._loadedYears[this.year] ? formatShare(calcShare(this.data[this.year])) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>
	        <div class="cell"></div>
		`;
	}

	_htmlMarket() {
		return html`
			<div class="cell head border">Market</div>
            <div class="cell head border">Value | Factor (%)</div>
            <div class="cell head border">${formatMoney(totalMarket(this.data.Markets))}</div>

            <!-- Market Companies -->
            ${repeat(this.data.Markets, market => market.ID, (market, index) => this._marketCells(market, index))}
            
            <!-- Market Companies Add -->
            ${this.mode === "write" ? html`
                <div class="cell">
	                <span class="button" @click=${() => this.add()}>+ Add company</span>
                </div>
                <div class="cell"></div>
                <div class="cell"></div>
			` : nothing}
		`;
	}

	_marketCells(market, index) {
		return html`${this.mode === "write" ? this._marketCellWrite(market, index) : this._marketCellRead(market)}`;
	}

	_marketCellRead(market) {
		return html`
			<div class="cell">${market.CompanyName}</div>
            <div class="cell">${formatMoney(market.Value)} | ${formatFactor(market.Factor)}</div>
            <div class="cell comment">${market.Comment}</div>
		`;
	}

	_marketCellWrite(market, index) {
		return html`
			<div class="cell edit">
				<paper-input no-label-float placeholder="Name" .value=${ifDefined(market.CompanyName)} @change=${e => this._onMarketValueChange(e.target.value, market, "CompanyName")}></paper-input>
			</div>
            <div class="cell edit value">
                <number-input no-label-float placeholder="Value" allowed-pattern="[0-9]" required auto-validate .value=${ifDefined(market.Value)} @change=${e => this._onMarketValueChange(e.target.value, market, "Value")}></number-input>
                <span>|</span>
                <number-input no-label-float placeholder="Factor %" pattern="^[0-9]+((\.|,)[0-9]{1,2})?$" allowed-pattern="[0-9.,]" required .value=${ifDefinedThen(market.Factor, fixFloating(market.Factor * 100))} @change=${e => this._onMarketValueChange(e.target.value / 100, market, "Factor")}></number-input>
			</div>
            <div class="cell comment">
                <paper-input no-label-float placeholder="Comment" .value=${ifDefined(market.Comment)} @change=${e => this._onMarketValueChange(e.target.value, market, "Comment")}></paper-input>
                <div class="buttons"></div>
                <paper-icon-button icon="delete" @click=${() => this.removeByIndex(index)}></paper-icon-button>
            </div>
		`;
	}

}

customElements.define("profile-finance-valuation", ProfileFinanceValuation);